// Import React state, popup lifecycle helpers and portal rendering
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Import the components
import CreateButton from "global/components/CreateButton";
import { InfoButton } from "global/components/InfoButton";
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";

// Reuse the shared inline-store preview hook 
import { usePodInline } from "global/hooks/usePodInline";

// Import other bits
import { useToast } from "global/hooks/useToast";
import { CloseIcon } from "global/strich_icons/StrichIcons";

// Import utilities
import { copyCode } from "global/utilities/Copy";

// Import the styles for the generators and installation help popup
import "./ConnectionInlineShopping.css";



import InlineShoppingTutorial from "pages/tutorials/InlineShoppingTutorial";



// Use the public embed address for code intended for customers' websites, regardless of the Portal environment
const INLINE_EMBED_SCRIPT_URL = "https://assets.strichsuite.com/modules/pod_inline/embed.js";

// Keep the existing inline display settings together and preserve their current labels, defaults and available choices
const INLINE_SHOPPING_SETTINGS = [
	{ code: "pod_inline_title", name: "Storefront Title", description: "Main heading shown at the top of your inline store.", placeholder: "Bring The Artwork Home", defaultValue: "" },
	{ code: "pod_inline_subtitle", name: "Storefront Subtitle", description: "Smaller line of text shown above the storefront title.", placeholder: "Select a painting, then choose a product.", defaultValue: "" },
	{ code: "pod_inline_template", name: "Default Product Template", description: "Products offered unless an image specifies another template.", defaultValue: "", control: "template" },
	{ code: "pod_inline_product_count", name: "Show Product Count", description: "Show or hide the product count above the inline products.", defaultValue: "N", options: [{ value: "Y", label: "Show" }, { value: "N", label: "Hide" }] },
	{ code: "pod_inline_products_position", name: "Products Alignment", description: "Choose whether products align left, centre, or right.", defaultValue: "left", options: [{ value: "left", label: "Left" }, { value: "center", label: "Center" }, { value: "right", label: "Right" }] },
	{ code: "pod_inline_fit_fill", name: "Fit / Fill", description: "Choose the default image fitting style for products in your store.", defaultValue: "fit", options: [{ value: "fit", label: "Fit" }, { value: "fill", label: "Fill" }] },
	{ code: "pod_inline_fit_fill_toggle", name: "Fit / Fill Toggle", description: "Show or hide the fit / fill switch on the inline store.", defaultValue: "Y", options: [{ value: "Y", label: "Show" }, { value: "N", label: "Hide" }] },
	{ code: "pod_inline_top_gallery", name: "Top Gallery", description: "Show or hide the image gallery at the top of the inline store.", defaultValue: "N", options: [{ value: "Y", label: "Show" }, { value: "N", label: "Hide" }] },
	{ code: "pod_inline_floating", name: "Floating Products", description: "Show products in a popup from the image button instead of on the page.", defaultValue: "N", options: [{ value: "Y", label: "Enable" }, { value: "N", label: "Disable" }] },
	{ code: "pod_inline_floating_button_text", name: "Floating Button Text", description: "Text shown on the floating products button.", placeholder: "Shop Products", defaultValue: "", floatingOnly: true },
	{ code: "pod_inline_floating_button_left", name: "Button From Left %", description: "How far from the left the floating button should appear.", placeholder: "50", defaultValue: "", floatingOnly: true },
	{ code: "pod_inline_floating_button_top", name: "Button From Top %", description: "How far from the top the floating button should appear.", placeholder: "50", defaultValue: "", floatingOnly: true },
	{ code: "pod_inline_country_currency_dropdown", name: "Country / Currency", description: "Show or hide the country and currency dropdowns above the products.", defaultValue: "Y", options: [{ value: "Y", label: "Show" }, { value: "N", label: "Hide" }] },
	{ code: "pod_inline_separate_child_products", name: "Separate Child Products", description: "Show child products as separate items instead of using a dropdown.", defaultValue: "N", options: [{ value: "Y", label: "Show Separately" }, { value: "N", label: "Use Dropdown" }] },
];

// Escape values entered by the seller before inserting them into double-quoted HTML attributes
const EscapeHtmlAttribute = (value) => {

	return String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

};



// Show site-wide installation guidance in the Portal's existing modal style
const InlineShoppingInstallHelp = ({ onClose }) => {

	// Focus the close control when the popup opens
	const closeButtonRef = useRef(null);

	// Allow Escape to close the popup and prevent the underlying store editor from scrolling
	useEffect(() => {

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		closeButtonRef.current?.focus();

		// Handle the keyboard shortcut while this popup is visible
		const handleKeyDown = (event) => {

			if (event.key === "Escape") { onClose(); }

		};

		// Restore the original page behaviour when the popup closes
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = previousOverflow;
		};

	}, [onClose]);

	// Dismiss when the dark backdrop is clicked, but not when its contents are clicked
	const handleOverlayClick = (event) => {

		if (event.target === event.currentTarget) { onClose(); }

	};

	// Render outside the store editor so the help popup can sit above its existing modal
	return createPortal(

		<div className="popup-overlay connection-inline-shopping__help-overlay" onClick={handleOverlayClick}>

			<div className="popup-container connection-inline-shopping__help-dialog" role="dialog" aria-modal="true" aria-labelledby="inline-shopping-help-title">

				{/* Reuse the standard Portal popup header and close button */}
				<div className="popup__header">
					<div className="popup__headerLeft">
						<h2 className="popup__title" id="inline-shopping-help-title">Where do I install the script?</h2>
					</div>
					<div className="popup__headerRight">
						<button ref={closeButtonRef} type="button" className="popup__close" onClick={onClose} title="Close" aria-label="Close installation help">
							<CloseIcon />
						</button>
					</div>
				</div>

				{/* Explain the difference between installing code once in a shared template and loading it on each page */}
				<div className="popup-content connection-inline-shopping__help-content">

					<p><strong>Install the script in one site-wide location</strong>, such as the global footer or shared page template. Your website will then include it automatically on each page. You don't need to paste it into every individual page.</p>
					<p>Only include the script <strong>once in any one page's HTML</strong>. The shoppable image and gallery tags still go in the content where you want them to appear.</p>

					{/* Give WordPress users a method that does not depend on the active theme */}
					<section>
						<h3>WordPress</h3>
						<p>Use your theme's global custom-code area or a header/footer code plugin. Add the generated script to the site-wide <strong>footer</strong> section and save it. For each shoppable page, use a <strong>Custom HTML</strong> block for your image and optional gallery code.</p>
						<p className="connection-inline-shopping__help-note">Some WordPress.com plans and user roles restrict custom scripts.</p>
						<a href="https://wordpress.org/documentation/article/custom-html/" target="_blank" rel="noopener noreferrer">WordPress Custom HTML guide</a>
					</section>

					{/* Webflow provides a site-wide footer injection field */}
					<section>
						<h3>Webflow</h3>
						<p>Open <strong>Site settings → Custom code → Footer code</strong>, paste the script, save and publish your site. Add the image and gallery markup where those elements should appear.</p>
						<a href="https://help.webflow.com/hc/en-us/articles/33961357265299-Custom-code-in-head-and-body-tags" target="_blank" rel="noopener noreferrer">Webflow custom-code guide</a>
					</section>

					{/* Cover simple websites and custom-built applications without introducing a framework dependency */}
					<section>
						<h3>Other websites / custom HTML</h3>
						<p>Paste the script into your shared footer, layout or page template, preferably just before <code>&lt;/body&gt;</code>. Put the generated image tag in your page content, and the gallery placeholder wherever the product choices should appear.</p>
					</section>

					{/* Keep the placement caveat visible for platforms which isolate embedded HTML */}
					<p className="connection-inline-shopping__help-note">The image, gallery and script must belong to the same page document. HTML inside an iframe is separate from the surrounding website.</p>

				</div>

			</div>

		</div>,
		document.body

	);

};



// Merge saved settings with draft settings so preview helpers can read one current snapshot by code.
const MergeSettingsByCode = (savedSettings = [], draftSettings = []) => {

	// Use a map so later draft settings overwrite earlier saved settings by code.
	const mergedSettingsByCode = new Map();

	// Add saved settings first so they can be overwritten by draft settings when the same code exists in both.
	(Array.isArray(savedSettings) ? savedSettings : []).forEach((setting) => {

		// Normalise the code before using it as the map key.
		const safeCode = String(setting?.code || "").trim();

		// Stop here when the code is empty so we do not pollute the map with invalid keys.
		if (safeCode) {
			// Add the saved setting to the map so it can be overwritten by a draft setting with the same code.
			mergedSettingsByCode.set(safeCode, setting);
		}

	});

	// Add draft settings next so they can overwrite saved settings when the same code exists in both.
	(Array.isArray(draftSettings) ? draftSettings : []).forEach((setting) => {

		// Draft values win when they share the same code as a saved value.
		const safeCode = String(setting?.code || "").trim();

		// Stop here when the code is empty so we do not pollute the map with invalid keys.
		if (safeCode) {
			// Add the draft setting to the map so it can overwrite a saved setting with the same code.
			mergedSettingsByCode.set(safeCode, setting);
		}

	});

	// Return one merged settings snapshot that preview helpers can read consistently.
	return Array.from(mergedSettingsByCode.values());

};



// Build the store's reusable installation script and page-specific HTML snippets
const ConnectionInlineShopping = ({ storeAlias, availablePodTemplates, formSettings, savedSettings, handleSettingValueChange, previewAlias, addingNewConnection }) => {




	// Full iframe rebuilds should still use the latest unsaved draft values for colours, copy, and typography.
	const previewRenderSettings = useMemo(() => (
		MergeSettingsByCode(savedSettings)
	), [savedSettings]);

	// The shared hook builds the iframe HTML and returns the loading / ready state for the preview.
	const {
		previewFrameRef,
		previewSrcDoc,
		isLoading: podInlineIsLoading,
		error: podInlineError,
		status: podInlineStatus,
	} = usePodInline({
		enabled:  !addingNewConnection && Boolean(previewAlias),
		alias: "portalsettings",
		settings: previewRenderSettings,
	});

	// Once the current iframe instance has rendered, keep it visible during live in-place updates.
	const [hasPreviewRenderedOnce, ] = useState(false);

	// Only show the blocking loading state before the current preview instance has rendered once.
    const showPreviewLoadingState = !hasPreviewRenderedOnce && (!previewSrcDoc || podInlineIsLoading || podInlineStatus === "loading");

	// Resolve unsaved settings first, then the saved store setting, then the original control default
	const getInlineSettingValue = (code, defaultValue = "") => {

		const draftSetting = formSettings.find((setting) => setting.code === code);
		if (draftSetting && draftSetting.value !== null && draftSetting.value !== undefined) { return String(draftSetting.value); }

		const savedSetting = savedSettings.find((setting) => setting.code === code);
		if (savedSetting && savedSetting.value !== null && savedSetting.value !== undefined) { return String(savedSetting.value); }

		return defaultValue;

	};

    // Resolve the selected product template from the store settings
	const defaultTemplateCode = getInlineSettingValue("pod_inline_template");

	// Write changes into the same store draft used by Styling and the existing Save Changes action
	const updateInlineSetting = (setting, nextValue) => {

		handleSettingValueChange("POD Inline", setting.code, setting.name, setting.description, "text")(nextValue);

	};

	// Only show floating-button placement controls while the floating product picker is enabled
	const floatingProductsEnabled = getInlineSettingValue("pod_inline_floating", "N") === "Y";

	// Store image-specific builder choices locally so they never change the saved store settings
	const [imageUrl, setImageUrl] = useState("");
	const [imageAlt, setImageAlt] = useState("");
	const [imageTemplateCode, setImageTemplateCode] = useState("");
	const [imageCoverage, setImageCoverage] = useState("");
	const [imagePrintFile, setImagePrintFile] = useState("");

	// Track whether the installation instructions are open
	const [showInstallHelp, setShowInstallHelp] = useState(false);
	const helpButtonRef = useRef(null);

	// Show success or error feedback using the Portal's existing notifications
	const { toastSuccess, toastError } = useToast();

	// Build the available product-template choices using the English title where possible
	const templateOptions = availablePodTemplates.map((template) => {

		const code = String(template.code || "").trim();
		let title = code;
		let englishTranslation = null;

		if (Array.isArray(template.translations)) { englishTranslation = template.translations.find((translation) => translation.language === "EN"); }
		if (template.title) { title = String(template.title); }
		if (englishTranslation && englishTranslation.title) { title = String(englishTranslation.title); }

		return { code, title };

	}).filter((template) => template.code);

	// Preserve an existing default even if that template is missing from the current account list
	const defaultTemplateIsListed = templateOptions.some((template) => template.code === defaultTemplateCode);

	// Generate the same customer-facing embed URL from development and production Portals
	const scriptCode = `<script\n  src="${INLINE_EMBED_SCRIPT_URL}"\n  data-strich-store="${EscapeHtmlAttribute(storeAlias)}"\n  async\n></script>`;

	// Start with a complete HTML image tag rather than attributes which need manual editing
	const imageTagLines = [
		"<img",
		`  src="${EscapeHtmlAttribute(imageUrl.trim())}"`,
		`  alt="${EscapeHtmlAttribute(imageAlt.trim())}"`,
		"  data-strich-shoppable-image",
	];

	// Include optional per-image overrides only when the seller has chosen them
	if (imageTemplateCode) { imageTagLines.push(`  data-strich-template="${EscapeHtmlAttribute(imageTemplateCode)}"`); }
	if (imageCoverage) { imageTagLines.push(`  data-strich-coverage="${imageCoverage}"`); }
	if (imagePrintFile.trim()) { imageTagLines.push(`  data-strich-pod-print-file="${EscapeHtmlAttribute(imagePrintFile.trim())}"`); }

	// Finish the image tag and provide a separate optional gallery placeholder
	imageTagLines.push(">");
	const imageCode = imageTagLines.join("\n");
	const galleryCode = "<div data-strich-product-gallery></div>";

	// Return keyboard focus to the installation-help button when its popup closes
	const closeInstallHelp = () => {

		setShowInstallHelp(false);
		helpButtonRef.current?.focus();

	};

	return (

		<div className="connection-inline-shopping">

			{/* General Inline Settings */}
			<div className="card">

				<div className="card__header">

					<h3>Inline Shopping Settings</h3>

				</div>

				<div className="card__content">

					<div className="connection-inline-shopping__settings-and-preview">

						<div className="connection-inline-shopping__settings">

							{/* Render each setting with its input and contextual help */}
							{INLINE_SHOPPING_SETTINGS.filter((setting) => !setting.floatingOnly || floatingProductsEnabled).map((setting) => (

								<div key={setting.code} className={setting.floatingOnly ? "connection-inline-shopping__setting connection-inline-shopping__setting--floating" : "connection-inline-shopping__setting"}>

									<div className="connection-inline-shopping__setting-label">

										<label className="connection-inline-shopping__label" htmlFor={`inline-setting-${setting.code}`}>{setting.name}</label>

										<InfoButton shortLabel={setting.name} description={setting.description} />

									</div>

										{/* Populate the template selector from the store's available POD templates */}
										{setting.control === "template" ? (

											<select id={`inline-setting-${setting.code}`} value={defaultTemplateCode} onChange={(event) => handleSettingValueChange("POD Inline", "pod_inline_template", "Store Template", "Default template code to use for this inline store.", "text")(event.target.value)}>
												
												<option value="" disabled>Select a template</option>

												{defaultTemplateCode && !defaultTemplateIsListed && <option value={defaultTemplateCode}>{defaultTemplateCode} (Current)</option>}

												{templateOptions.map((template) => <option key={template.code} value={template.code}>{template.title}</option>)}

											</select>

										) : setting.options ? (

										<select id={`inline-setting-${setting.code}`} value={getInlineSettingValue(setting.code, setting.defaultValue)} onChange={(event) => updateInlineSetting(setting, event.target.value)}>

											{setting.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}

										</select>

									) : (

										<input id={`inline-setting-${setting.code}`} type="text" value={getInlineSettingValue(setting.code, setting.defaultValue)} placeholder={setting.placeholder || ""} onChange={(event) => updateInlineSetting(setting, event.target.value)} />
									
									)}

								</div>

							))}

						</div>

						{templateOptions.length === 0 && <p className="hintText">Create a POD template before choosing a default.</p>}

						<div>

							{/* Show a blocking loading state until the current iframe instance has rendered once. */}
							{showPreviewLoadingState &&

								<div className="connection-popup-preview-status">

									<LoadingSpinner />

									<span>Loading inline store preview...</span>

								</div>

							}

							{/* Render the live preview iframe once the hook has produced the current srcDoc. */}
							{previewSrcDoc &&

								<iframe
									key="product"
									ref={previewFrameRef}
									title={`Inline store preview for ${previewAlias}`}
									className={`connection-popup-preview-frame${showPreviewLoadingState ? " connection-popup-preview-frame--hidden" : ""} connection-popup-inline-shopping-preview-frame`}
									srcDoc={previewSrcDoc}
									data-strich-preview-surface="inline-shopping"
								/>

							}

							{/* Surface iframe/bootstrap errors from the shared preview hook in the preview stage. */}
							{podInlineStatus === "error" &&

								<div className="connection-popup-preview-status connection-popup-preview-status--error">

									{podInlineError}

								</div>

							}
							
						</div>
					
					</div>

				</div>
		
			</div>



			<div className="card">

				<div style={{padding: "18px 15px 10px"}}>

					<h2>Add Inline Shopping to Your Website</h2>
					<br/>
					<p>Make your website shoppable in just a few simple steps! Use the sections below to generate and copy the code you need to integrate StrichSuite Inline Shopping into your website.</p>	
					<br/>
					<p><b>Need a little extra help?</b> Click <b>Tutorial</b> for a beginner-friendly, step-by-step guide with detailed instructions, helpful tips and explanations of where to add each piece of code.</p>
					<br/>
					<div className="connection-inline-shopping__actions">

						<InlineShoppingTutorial 
							popupTitle="Add Inline Shopping to your website" 
							popupSubTitle="Three simple steps to make your images shoppable" 
							storeId={"elles-test-store"} 
							imageTag={""} 
							floatingOnly={false} 
						/>

					</div>
				
				</div>

			</div>



			{/* Generate the one script users install in their shared website header or footer */}
			<div className="card">
				<div className="card__header">
					<h3>1. Install Inline Shopping</h3>
					<span className="hintText">Add this once in your website's site-wide footer or shared layout. It will then load automatically on the pages that need it.</span>
				</div>
				<div className="card__content">
					<textarea className="connection-inline-shopping__code" readOnly rows={5} value={scriptCode} onFocus={(event) => event.target.select()} aria-label="Store installation script" />
					<div className="connection-inline-shopping__actions">
						<button ref={helpButtonRef} type="button" className="connection-inline-shopping__help-button" onClick={() => setShowInstallHelp(true)}>Where do I put this?</button>
						<CreateButton size="s" title="Copy Script" onClick={() => copyCode("Embed script", scriptCode, toastSuccess, toastError)} disabled={!storeAlias} />
					</div>
					{!storeAlias && <p className="hintText">Set your store alias before copying the installation script.</p>}
				</div>
			</div>

			{/* Generate an entire image tag using the seller's artwork and optional overrides */}
			<div className="card">
				<div className="card__header">
					<h3>2. Make an Image Shoppable</h3>
					<span className="hintText">Enter your image details, choose any overrides and copy the complete image tag into your website.</span>
				</div>
				<div className="card__content">
					<div className="connection-inline-shopping__fields">

						{/* The image source is required for a ready-to-use HTML image element */}
						<div>
							<label className="connection-inline-shopping__label" htmlFor="inline-image-url">Image URL</label>
							<input id="inline-image-url" type="text" value={imageUrl} onChange={(event) => setImageUrl(event.target.value)} placeholder="https://example.com/artwork.jpg" />
						</div>

						{/* Keep alternative text editable for accessible image descriptions */}
						<div>
							<label className="connection-inline-shopping__label" htmlFor="inline-image-alt">Image description (alt text)</label>
							<input id="inline-image-alt" type="text" value={imageAlt} onChange={(event) => setImageAlt(event.target.value)} placeholder="Comic page" />
						</div>

						{/* An empty override uses the store's saved product template */}
						<div>
							<label className="connection-inline-shopping__label" htmlFor="inline-image-template">Product template</label>
							<select id="inline-image-template" value={imageTemplateCode} onChange={(event) => setImageTemplateCode(event.target.value)}>
								<option value="">Use store default</option>
								{templateOptions.map((template) => <option key={template.code} value={template.code}>{template.title}</option>)}
							</select>
						</div>

						{/* An empty coverage choice leaves the store's fit/fill setting in place */}
						<div>
							<label className="connection-inline-shopping__label" htmlFor="inline-image-coverage">Artwork coverage</label>
							<select id="inline-image-coverage" value={imageCoverage} onChange={(event) => setImageCoverage(event.target.value)}>
								<option value="">Use store default</option>
								<option value="fit">Fit</option>
								<option value="fill">Fill</option>
							</select>
						</div>

						{/* A separate print file name is optional and only changes this image's source artwork */}
						<div className="connection-inline-shopping__field-wide">
							<label className="connection-inline-shopping__label" htmlFor="inline-image-print-file">High-resolution print file name (optional)</label>
							<input id="inline-image-print-file" type="text" value={imagePrintFile} onChange={(event) => setImagePrintFile(event.target.value)} placeholder="Comic Page High Res" />
						</div>
					</div>

					{/* Always show the generated HTML, but require an image source before copying */}
					<textarea className="connection-inline-shopping__code" readOnly rows={imageTagLines.length + 1} value={imageCode} onFocus={(event) => event.target.select()} aria-label="Generated shoppable image HTML" />
					<div className="connection-inline-shopping__actions">
						<CreateButton size="s" title="Copy Image Tag" onClick={() => copyCode("Image tag", imageCode, toastSuccess, toastError)} disabled={!imageUrl.trim()} />
					</div>
					{!imageUrl.trim() && <p className="hintText">Enter an image URL to enable copying.</p>}
				</div>
			</div>

			{/* Give the seller a separate insertion point for an on-page product gallery */}
			<div className="card">
				<div className="card__header">
					<h3>3. Position the Product Gallery</h3>
					<span className="hintText">Place this wherever you want products to appear on the page. It isn't needed for floating-only mode.</span>
				</div>
				<div className="card__content">
					<textarea className="connection-inline-shopping__code" readOnly rows={2} value={galleryCode} onFocus={(event) => event.target.select()} aria-label="Product gallery HTML" />
					<div className="connection-inline-shopping__actions">
						<CreateButton size="s" title="Copy Gallery" onClick={() => copyCode("Gallery placeholder", galleryCode, toastSuccess, toastError)} />
					</div>
				</div>
			</div>

			{/* Mount the installation popup only when the seller requests platform-specific help */}
			{showInstallHelp && <InlineShoppingInstallHelp onClose={closeInstallHelp} />}

		</div>
	);

};

export default ConnectionInlineShopping;